import { memo } from 'react'

import { LinkOutbound } from '@/components/common/Link'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { type CardType } from '@/defs/card'
import { type IconType } from '@/defs/icon'
import { PRICE_SOURCE_CURRENCIES, type PriceSourceType } from '@/defs/price'
import { getPriceSourceLink } from '@/utils/brandUtil'
import { cn } from '@/utils/commonUtil'
import { formatPrice } from '@/utils/formatUtil'

import styles from './PriceSource.module.scss'
import { PriceSourceTCGPlayer } from './PriceSourceTCGPlayer/PriceSourceTCGPlayer'

type ConfigType = {
  ariaLabel: string
  icon?: IconType
}

const CONFIG: Record<PriceSourceType, ConfigType> = {
  cardhoarder: { ariaLabel: 'Buy at Cardhoarder' },
  cardkingdom: { ariaLabel: 'Buy at Card Kingdom', icon: 'brand-cardkingdom' },
  cardmarket: { ariaLabel: 'Buy at Cardmarket', icon: 'brand-cardmarket' },
  cardsphere: { ariaLabel: 'Buy at Cardsphere', icon: 'brand-cardsphere' },
  cardtrader: { ariaLabel: 'Buy at CardTrader', icon: 'brand-cardtrader' },
  face2face: { ariaLabel: 'Buy at Face to Face Games', icon: 'brand-face2face' },
  manapool: { ariaLabel: 'Buy at Mana Pool', icon: 'brand-manapool' },
  mtgstocks: { ariaLabel: 'MTGStocks' },
  scg: { ariaLabel: 'Buy at Star City Games', icon: 'brand-starcitygames' },
  tcgplayer: { ariaLabel: 'Buy at TCGplayer', icon: 'brand-tcgplayer' },
}

type Props = {
  card: CardType
  colorScheme?: 'light'
  priceSource: PriceSourceType
  size?: 'lg'
}

const PriceSourceInner = ({ card, colorScheme, priceSource, size }: Props) => {
  const { name, priceSources } = card
  const { price, slug } = priceSources?.[priceSource] || {}

  const className = cn(styles.container, { colorScheme, size, styles })
  const href = getPriceSourceLink({ name, priceSource, slug })

  if (priceSource === 'tcgplayer') {
    return <PriceSourceTCGPlayer {...CONFIG.tcgplayer} className={className} href={href} price={price} />
  }

  const { ariaLabel, icon } = CONFIG[priceSource]
  const priceDisplay = formatPrice(price, PRICE_SOURCE_CURRENCIES[priceSource])

  const title = `${ariaLabel} (${priceDisplay})`

  return (
    <LinkOutbound aria-label={title} className={className} href={href} title={title} utm={false}>
      <IconWithLabel iconBefore={icon} iconBeforeMargin='sm' iconBeforeSize='sm'>
        {priceDisplay}
      </IconWithLabel>
    </LinkOutbound>
  )
}

export const PriceSource = memo(PriceSourceInner)
